import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

const stats = [
  { value: '5000+', label: '认证主播' },
  { value: '2000万+', label: '注册用户' },
  { value: '99.9%', label: '服务可用率' },
  { value: '8', label: '直播分类' },
];

const testimonials = [
  {
    name: '张先生',
    role: '资深用户 · 使用2年',
    content: '直播互动平台的画质和稳定性真的很棒！我每天都会在这里看才艺直播，互动体验也非常流畅，强烈推荐给大家。',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20passport%20photo%20with%20warm%20lighting%2C%20friendly%20expression%2C%20clean%20white%20background%2C%20professional%20headshot&width=120&height=120&seq=testimonial-01&orientation=squarish',
  },
  {
    name: '李女士',
    role: '认证主播 · 才艺表演',
    content: '作为一名主播，我非常感谢平台提供的支持和机会。这里的观众很热情，平台的分成也很合理，让我能够将爱好变成事业。',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20passport%20photo%20with%20warm%20lighting%2C%20confident%20smile%2C%20clean%20white%20background%2C%20professional%20headshot&width=120&height=120&seq=testimonial-02&orientation=squarish',
  },
  {
    name: '王同学',
    role: '大学生用户 · 游戏直播爱好者',
    content: '课余时间最喜欢在这里看游戏直播，主播们水平都很高，弹幕文化也很有趣。安全方面也让人放心，推荐给了很多同学。',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20young%20Chinese%20male%20passport%20photo%20with%20warm%20lighting%2C%20friendly%20smile%2C%20clean%20white%20background%2C%20professional%20headshot&width=120&height=120&seq=testimonial-03&orientation=squarish',
  },
];

const milestones = [
  { year: '2023', title: '平台创立', description: '核心团队组建完成，直播互动体验平台正式立项启动研发。' },
  { year: '2024 Q1', title: 'Beta版本上线', description: '完成核心直播功能开发，开启小范围内测，收集用户反馈。' },
  { year: '2024 Q3', title: '正式发布', description: '平台正式面向公众开放，首月注册用户突破50万。' },
  { year: '2025 Q1', title: '用户破千万', description: '平台注册用户突破1000万，认证主播超过3000人。' },
  { year: '2025 Q2', title: '技术升级', description: '全面升级至4K超高清直播，引入AI智能推荐和审核系统。' },
];

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 直播互动体验平台"
        description="直播互动体验平台致力于打造安全可靠的直播娱乐社区。了解我们的品牌故事、发展历程和核心价值观，感受专业的直播互动服务。"
        keywords="关于我们,直播互动平台,品牌介绍,直播平台口碑,联系方式"
        canonicalUrl="https://rtxrj.cn/about"
        ogTitle="关于我们 - 直播互动体验平台"
        ogDescription="了解直播互动体验平台的品牌故事、发展历程和核心价值观。"
        ogUrl="https://rtxrj.cn/about"
        schemaData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于直播互动体验平台',
          description: '直播互动体验平台品牌介绍与发展历程',
          url: 'https://rtxrj.cn/about',
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="w-full px-4 md:px-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          {/* Brand Story */}
          <section className="w-full py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-serif mb-4">
                    关于<strong>直播互动</strong>
                  </h1>
                  <p className="text-foreground-600 text-sm max-w-xl mx-auto leading-relaxed">
                    我们是一家专注于直播互动娱乐的科技公司，致力于打造安全、可靠、高质量的在线直播社区平台。
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-serif mb-4">
                      我们的<strong>故事</strong>
                    </h2>
                    <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
                      <p>
                        直播互动体验平台创立于2023年，由一群热爱直播文化的技术专家和内容创作者共同发起。我们深刻理解用户对于高质量直播互动内容的渴望，也看到了行业中存在的安全和体验痛点。
                      </p>
                      <p>
                        从最初的小团队起步，我们始终坚持"用户至上、安全第一"的核心理念。通过持续的技术创新和严格的内容审核机制，平台逐步赢得了用户的信任和喜爱。
                      </p>
                      <p>
                        如今，我们已经成长为拥有超过两千万注册用户、五千多名认证主播的综合直播互动平台。未来，我们将继续深耕直播技术，拓展更多垂直领域，为全球用户提供更加丰富多元的直播娱乐体验。
                      </p>
                    </div>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20tech%20office%20team%20collaboration%20scene%20with%20diverse%20professionals%20working%20on%20large%20screens%20displaying%20live%20streaming%20analytics%2C%20warm%20natural%20light%20from%20windows%2C%20creative%20workspace%20with%20plants%2C%20professional%20photography%20with%20coral%20and%20warm%20accents&width=800&height=600&seq=about-story-01&orientation=landscape"
                      alt="直播互动体验平台团队"
                      title="直播互动体验平台 - 专业团队"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                  {stats.map((stat) => (
                    <div
                      key={stat.label}
                      className="bg-background-50 rounded-lg p-6 text-center"
                    >
                      <div className="text-2xl md:text-3xl font-bold text-primary-500 font-serif mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif text-center mb-12">
                  发展<strong>历程</strong>
                </h2>
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
                  <div className="flex flex-col gap-8">
                    {milestones.map((milestone, index) => (
                      <div
                        key={milestone.year}
                        className={`flex flex-col md:flex-row gap-4 md:gap-8 ${
                          index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                        }`}
                      >
                        <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                          <div className="bg-background-100 rounded-lg p-5">
                            <span className="inline-block px-2 py-1 text-xs font-bold text-accent-700 bg-accent-100 rounded-full mb-2">
                              {milestone.year}
                            </span>
                            <h3 className="text-base font-semibold text-foreground-900 mb-1">
                              {milestone.title}
                            </h3>
                            <p className="text-sm text-foreground-600 leading-relaxed">
                              {milestone.description}
                            </p>
                          </div>
                        </div>
                        <div className="hidden md:flex items-center justify-center flex-shrink-0">
                          <div className="w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50"></div>
                        </div>
                        <div className="flex-1 hidden md:block"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif text-center mb-12">
                  用户<strong>口碑</strong>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {testimonials.map((testimonial) => (
                    <div
                      key={testimonial.name}
                      className="bg-background-50 rounded-lg p-6"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">
                            {testimonial.name}
                          </div>
                          <div className="text-xs text-foreground-500">
                            {testimonial.role}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        &ldquo;{testimonial.content}&rdquo;
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <ContactSection />
        </main>
        <Footer />
      </div>
    </>
  );
}

function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u4tktca23r0tr1aqlg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  const charLimit = 500;

  return (
    <section id="contact" className="w-full py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif text-center mb-3">
            <strong>联系</strong>我们
          </h2>
          <p className="text-foreground-600 text-sm text-center mb-10">
            如有任何疑问或合作意向，欢迎通过以下方式联系我们
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <div className="bg-background-100 rounded-lg p-5 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
              <p className="text-xs text-foreground-600">contact@zhubohudong.com</p>
            </div>
            <div className="bg-background-100 rounded-lg p-5 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                <i className="ri-phone-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
              <p className="text-xs text-foreground-600">400-888-8888</p>
            </div>
            <div className="bg-background-100 rounded-lg p-5 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
              <p className="text-xs text-foreground-600">北京市朝阳区科技园区</p>
            </div>
          </div>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-background-100 rounded-lg p-6 md:p-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                主题 <span className="text-primary-500">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                required
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
                placeholder="请输入咨询主题"
              />
            </div>
            <div className="mb-1">
              <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                留言内容 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={charLimit}
                rows={5}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200 resize-none"
                placeholder="请输入您的留言内容（最多500字）"
              ></textarea>
            </div>
            <p className="text-xs text-foreground-400 text-right mb-4">最多{charLimit}字</p>

            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />

            <button
              type="submit"
              disabled={formStatus === 'submitting'}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-semibold rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              {formStatus === 'submitting' ? '提交中...' : '发送留言'}
            </button>

            {formStatus === 'success' && (
              <p className="mt-4 text-sm text-green-600 text-center">{formMessage}</p>
            )}
            {formStatus === 'error' && (
              <p className="mt-4 text-sm text-red-600 text-center">{formMessage}</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}